import React, { useContext, useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { useQueryClient } from "@tanstack/react-query";
import {
  TextField,
  Grid,
  Box,
  Typography,
  Tabs,
  Tab,
  InputAdornment,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import AssigneeIcon from "../../assets/action-items/assignee-icon.svg";
import ClientIcon from "../../assets/action-items/client-icon.svg";
import CoachIcon from "../../assets/action-items/coach-icon.svg";
import DueDateIcon from "../../assets/action-items/due-date-calendar.svg";
import SponserIcon from "../../assets/action-items/sponser-icon.svg";
import StatusIcon from "../../assets/action-items/status-icon.svg";
import SubscribeIcon from "../../assets/action-items/subscribe-icon.svg";
import DefaultFlagIcon from "../../assets/action-items/default-flag.svg";
import CriticalFlag from "../../assets/action-items/red-rated-flag-icon.svg";
import HighRiskFlag from "../../assets/action-items/high-priority-flag.svg";
import MediumRiskFlag from "../../assets/action-items/medium-priority-flag.svg";
import LowRiskFlag from "../../assets/action-items/low-priority-flag.svg";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import AddIcon from "../../assets/action-items/add-attachment.svg";
import LinkedInteraction from "./LinkedInteraction";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import {
  actionItemStatuses,
  ActionItemStatus,
  Option,
  FormData,
  Attachment,
  Client,
} from "../../utils/types";
import { getUserAttributes } from "../../utils/auth";
import ProgressSpinner from "../Common/ProgressSpinner";
import ActionItemNotes from "./ActionItemNotes";
// import { useLocation, useNavigate } from "react-router-dom";
import ActionItemService from "../../services/actionItems/ActionItemService";
import ActionItemNotesService from "../../services/actionItems/ActionItemNotes";
import "../../styles/actionItems.css";
import CalendarIcon from "../../assets/action-items/due-date-calendar-icon.svg";
import "../../styles/interactionDetailsPage.css";
import UserInteractionsService from "../../services/interactions/Interactions";
import EventNoteIcon from '@mui/icons-material/EventNote';
import RichTextEditor from "../Common/RichTextEditor";
import ClientService from "../../services/clients/ClientService";
import DetailRow from "../Common/DetailRow";
import UserSelector from "./UserSelector";
import PrioritySelector from "./PrioritySelector";
import AttachmentList from "./AttachmentList";
import StatusSelector from "./StatusSelector";
import TagListIcon from "../../assets/action-items/tag-list.svg";
import ActionItemsTagsCell from "./ActionItemsTagsCell";
import AccountSelector from "./AccountSelector";
import ActionItemHistory from "./ActionItemHistory";
import CommonDatePicker from "../Common/CommonDatePicker";
import { extractDomainFromEmail, getStatusOptions, SUPPORT_TASK_DEFAULTS } from "../../utils/utils";
import Delete from "../../assets/action-items/slideOver-delete.svg";
import CustomConfirmation from "../Common/CustomConfirmation";
import { GlobalContext } from "../../context/GlobalContext";
import EnterIcon from "../../assets/common/enter-arrow-icon.svg";
import BulkTasks from "./BulkTasks";
import ActionItemSubtasks from "./ActionItemSubtasks";
import { ActionItemBreadcrumbs } from "../../utils/ActionItemBreadcrumbs";
import {
  getAncestorBreadcrumbs,
  normalizeSubtasks,
  resolveIsSubTaskFlag,
  collectClampedSubTaskDueDates,
} from "../../utils/subtaskUtils";
import { ActionItemBreadcrumb, SubtaskItem } from "../../utils/types";
import { SlideOverHeaderLeft } from "../Common/SlideOver";
interface ActionItemData {
  actionItemId: string;
  description: string;
  dueDate?: string;
  assigneePorId?: string | null;
  status: string;
  attachments: any[];
  email_id?: string;
  title: string;
  coachPorId?: string;
  priority?: string | null;
  sponsorPorId?: string;
  actionSubscribersPorIds?: string[] | null;
  deleteSubscribersPorIds?: string[] | null;
  tags?: string[] | null;
  tagIds?: string[];
  addSpaces?: string[];
  deleteSpaces?: string[];
  clientOrgId: string;
  removeFields?: string[];
  titles?: string[];
  parentActionItemId?: string;
}

type NullableString = string | null;
type ISODateString = string; // we will store dates as ISO strings (or null)

interface DetailValues {
  priority: NullableString;
  status: string;
  assignee: NullableString;
  coach: NullableString;
  sponsor: NullableString;
  dueDate: ISODateString | null; // store ISO string or null
  actionItemTitle: string | null;
  actionItemDescription: string;
  subscribers: string[]; // array of email strings
  deleteSubscribers: string[]; 
  tags: string[];
  tagIds?: string[];
  spaces?: string[];
  client: {
    orgId: string | null;
    name: string | null;
  };
  subscribersTouched?: boolean;
}


interface ActionItemDetailsType {
  actionItemId: string;
  assignee?: string | null;
  createdBy?: string | null;
  coachProfileOrgId?: string | null;
  sponsorProfileOrgId?: string | null;
  clientOrgId?: string | null;
  actionItemTitle?: string | null;
  actionItemDescription?: string | null;
  interactionTitle?: string | null;
  attachments: any[]; // refine if you know the attachment shape
  client?: string | null;
  stage?: string | null;
  priority?: string | null;
  dueDate?: string | undefined;
  revisedDueDate?: string | Date | null;
  coachName?: string | null;
  coachEmail?: string | null;
  coachPictureUrl?: string | null;
  assigneeName?: string | null;
  assigneeEmail?: string | null;
  assigneePictureUrl?: string | null;
  sponsorName?: string | null;
  sponsorEmail?: string | null;
  sponsorPictureUrl?: string | null;
  assignorName?: string | null;
  assignorEmail?: string | null;
  assignorPictureUrl?: string | null;
  actionItemSubscribers?: any[]; // refine if you know subscriber shape
  deleteSubscribers?: string[] | null;
  actionItemAccount?: string | null;
  tags?: string[] | null;
  canEdit?: boolean;
  subscribedUserGroups?: string[];
  clientName?: string | null;
  isSupportTicket?: boolean;
  interactionId?: string | null;
  interactionDuration?: number | null;
  interactionStartTime?: string | null;
  isTask?: boolean;
  isSubTask?: boolean;
  parentActionItemId?: string;
  parentActionItemTitle?: string;
  parentDueDate?: string | null;
  parentActionItem?: { dueDate?: string | null } | null;
  subTasks?: any[];
}

interface ActionItemProps {
  actionItemType:
    | "MEETING_ACTION_ITEMS"
    | "MY_ACTION_ITEMS"
    | "TEAM_ACTION_ITEMS"
    | "SUBSCRIBED_ACTION_ITEMS";
  attendees?: any;
  meetingId?: string;
  selectedActionItem?: any;
  handleClose?: () => void;
  meetingDate?: string;
  isRhythm?: boolean;
  isPreview?: boolean;
  is_team_meeting_id?: boolean;
  selectedTab?: string;
  startEndMeetingTime?: { start_time: string; end_time: string };
  view?: any;
  saveTrigger?: boolean;
  discardTrigger?: boolean;
  resetSaveTrigger?: () => void;
  resetDiscardTrigger?: () => void;
  openDialog?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  isNewActionItem?: boolean;
  setActionItemRowDetails?: React.Dispatch<any>
  setOpenDialog?: React.Dispatch<React.SetStateAction<boolean>>
  groupByValues?: {
    groupByInteractionId: string | null;
    groupByClientDomain: { clientName: string | null; orgId: string | null } | null;
    groupByAssigneeId: string | null;
    groupByStatus: string | null;
  };
  tagsList?: any[];
  spacesList?: any[];
  isSupportTask?: boolean;
  onUnsavedChangesChange?: React.Dispatch<React.SetStateAction<boolean>>;
  bulkTasks?: boolean;
}
interface Attendee_details {
  email: string;
  name: string;
}
const ActionItem: React.FC<ActionItemProps> = ({
  actionItemType,
  attendees = [],
  meetingId,
  selectedActionItem,
  handleClose,
  isRhythm = false,
  // isPreview = false,
  // selectedTab,
  // startEndMeetingTime,
  // view,
  saveTrigger,
  discardTrigger,
  resetSaveTrigger,
  resetDiscardTrigger,
  openDialog,
  onValidityChange,
  isNewActionItem,
  setActionItemRowDetails,
  groupByValues,
  tagsList = [],
  spacesList = [],
  isSupportTask = false,
  onUnsavedChangesChange,
  bulkTasks,
}) => {
  const { showSnackbar } = useDisplaySnackbar();
  const queryClient = useQueryClient();
  const loggedInUserId = getUserAttributes()?.id;
  const loggedInEmail = getUserAttributes()?.email;
  const { accountDetails, impersonationDetails } = useContext(GlobalContext);
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const [_userPermission, setUserPermission] = useState("ALL");
  const [Attendees, setAttendees] = useState<Attendee_details[]>([]);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const dateAnchorRef = useRef<HTMLSpanElement | null>(null);
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [isActiveUsersReady, setIsActiveUsersReady] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const descriptionEditorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [clientList, setClientList] = useState<Client[]>([]);
  const [actionItemDetails, setActionItemDetails] = useState<ActionItemDetailsType | null>(null);
  const [selectedTab, setSelectedTab] = useState<"comments" | "history">("comments");
  const loggedInUserDomain = extractDomainFromEmail(loggedInEmail)?.toLocaleLowerCase();
  const isSupportMode = isSupportTask || actionItemDetails?.isSupportTicket === true;
  const [attachments, setAttachments] = useState<{
    [key: string]: Attachment;
  }>({});
  const [bulkTaskTitles, setBulkTaskTitles] = useState<string[]>([]);
  const STATUS_OPTIONS = getStatusOptions();
  const PRIORITY_OPTIONS = [
    { label: "Critical", value: "critical", icon: CriticalFlag },
    { label: "High",     value: "high",     icon: HighRiskFlag },
    { label: "Medium",   value: "medium",   icon: MediumRiskFlag },
    { label: "Low",      value: "low",      icon: LowRiskFlag },
  ];

  const [localTagsList, setLocalTagsList] = useState(tagsList);
  const [localSpacesList, setLocalSpacesList] = useState(spacesList);
  const [removedFields, setRemovedFields] = useState<string[]>([]);
  const [selectedInteractionId, setSelectedInteractionId] = useState<string | null>(null);
  const [showLinkedInteraction, setShowLinkedInteraction] = useState(
    !!selectedInteractionId
  );
  const isPersisted = !!actionItemDetails?.interactionId && !isNewActionItem;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const shouldRestoreTitleFocusRef = useRef(false);
  const leftSectionRef = useRef<HTMLDivElement | null>(null);
  const createAsSubtaskRef = useRef(false);
  const [viewedActionItemId, setViewedActionItemId] = useState<string | null>(null);
  const [breadcrumbTrail, setBreadcrumbTrail] = useState<ActionItemBreadcrumb[]>([]);
  const [isAddingSubtask, setIsAddingSubtask] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const [newSubtaskAssignee, setNewSubtaskAssignee] = useState<string | null>(null);
  const [newSubtaskDueDate, setNewSubtaskDueDate] = useState<string | null>(null);
  const [newSubtaskStatus, setNewSubtaskStatus] = useState<string>("to_do");

  useEffect(() => {
    if (!openDialog) {
      setViewedActionItemId(null);
      setBreadcrumbTrail([]);
      setIsAddingSubtask(false);
      setNewSubtaskTitle("");
      setNewSubtaskAssignee(null);
      setNewSubtaskDueDate(null);
      setNewSubtaskStatus("to_do");
      createAsSubtaskRef.current = false;
      return;
    }
    setViewedActionItemId(selectedActionItem?.id ?? null);
    setBreadcrumbTrail([]);
    setIsAddingSubtask(false);
    setNewSubtaskTitle("");
    setNewSubtaskAssignee(null);
    setNewSubtaskDueDate(null);
    setNewSubtaskStatus("to_do");
  }, [openDialog, selectedActionItem?.id]);

  const extractPorId = (item: any): string | null => {
    if (!item) return null;
    if (typeof item === "string") return item; // if already porId
    return item.porId ?? item.por_id ?? item.profileOrgRelationshipId ?? null;
  };

  const isAutoSavingRef = useRef(false);

  const focusTitleInput = () => {
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input || !openDialog) return;
      input.focus();
      const caretPosition = input.value?.length ?? 0;
      input.setSelectionRange?.(caretPosition, caretPosition);
      shouldRestoreTitleFocusRef.current = false;
    });
  };

  const [hasLeftSectionChanges, setHasLeftSectionChanges] = useState(false);
  const [hasRightSectionChanges, setHasRightSectionChanges] = useState(false);
  const hasRightSectionChangesRef = useRef(hasRightSectionChanges);
  
  useEffect(() => {
    hasRightSectionChangesRef.current = hasRightSectionChanges;
  }, [hasRightSectionChanges]);
  const EMPTY_DETAIL_VALUES = {
    priority: "",
    status: "to_do", // or "Todo" if you prefer a default
    assignee: null,
    coach: null,
    sponsor: null,
    dueDate: null,
    actionItemTitle: "",
    actionItemDescription: "",
    subscribers: [],
    deleteSubscribers: [],
    tags: [],
    addSpaces: [],
    deleteSpaces: [],
    client: {
      orgId: null,
      name: null,
    }
  };

  const normalizeAttachments = (
    attachments: { [key: string]: Attachment } | Attachment[] = []
  ) => {
    return Object.values(attachments)
      .map((a) => {
        if (a.url) {
          return {
            description: a.description?.trim() || "",
            url: a.url.trim(),
          };
        }

        // FILE attachment
        if (a.path && a.fileName) {
          return {
            path: a.path,
            fileName: a.fileName,
          };
        }

        return null;
      })
      .filter(Boolean)
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  };
  
  const [detailValues, setDetailValues] = useState<DetailValues>({
    priority: selectedActionItem?.priority ?? null,
    status: selectedActionItem?.status ?? "to_do",
    assignee: selectedActionItem?.assignee ?? null,
    coach: selectedActionItem?.coach ?? null,
    sponsor: selectedActionItem?.sponsor ?? null,
    dueDate: selectedActionItem?.dueDate ?? null,
    actionItemTitle: selectedActionItem?.description,
    actionItemDescription: selectedActionItem?.actionItemDescription ?? "",
    subscribers: (selectedActionItem?.subscribers ?? [])
      .map((s: any) => extractPorId(s))
      .filter((id: any): id is string => !!id),
    deleteSubscribers: selectedActionItem?.deleteSubscribers ?? [],
    tags: Array.isArray(selectedActionItem?.tags)
      ? selectedActionItem.tags.map((t: any) => t.id ?? t)
      : [],
    spaces: (selectedActionItem?.spaces || []),
    client: {
      orgId: selectedActionItem?.clientOrgId ?? null,
      name: selectedActionItem?.clientName ?? null
    }, 
    subscribersTouched: true,
  });

  const findUserInActiveUsers = (userList: any[], idOrEmail: string | null | undefined) => {
    if (!idOrEmail || !userList || userList.length === 0) return null;
    const target = String(idOrEmail).toLowerCase().trim();
    return userList.find((u) => {
      const porId = u?.profileOrgRelationshipId ?? u?.porId ?? u?.por_id;
      const profileOrgId = u?.profileOrgId ?? u?.id;
      const email = u?.email;
      return (
        (porId && String(porId).toLowerCase() === target) ||
        (profileOrgId && String(profileOrgId).toLowerCase() === target) ||
        (email && String(email).toLowerCase().trim() === target)
      );
    }) || null;
  };

  useEffect(() => {
    if (!openDialog) return;
    let valid = false;
  
    const normalizeDate = (date: any) =>
      date ? new Date(date).toISOString().split("T")[0] : null;
    const hasTaskTitle = bulkTasks
      ? bulkTaskTitles?.length > 0
      : !!detailValues.actionItemTitle?.trim();
    const hasDueDate = !!detailValues.dueDate;
    const hasPriority = !!detailValues.priority;
    const hasStatus = !!detailValues.status;
    const hasAssignee = isActiveUsersReady
      ? !!findUserInActiveUsers(activeUsers, detailValues.assignee)
      : false;

    const allRequiredFieldsPresent =
      hasTaskTitle &&
      hasDueDate &&
      hasPriority &&
      hasStatus &&
      hasAssignee;

    if (isNewActionItem || !actionItemDetails) {
      // NEW ACTION ITEM — check only required fields
      valid = allRequiredFieldsPresent;
    } else {
      // EXISTING ACTION ITEM — normalize both sides before comparison
      const current = {
        title: detailValues.actionItemTitle?.trim() || "",
        description: detailValues.actionItemDescription || "",
        priority: detailValues.priority || null,
        status: detailValues.status || null,
        assignee: detailValues.assignee || null,
        coach: detailValues.coach || null,
        sponsor: detailValues.sponsor || null,
        account: detailValues.client.name || null,
        dueDate: normalizeDate(detailValues.dueDate),
        subscribers: (detailValues.subscribers || []).map((s) => s.toLowerCase()).sort(),
        tags: (detailValues.tags || []).sort(),
        attachments: normalizeAttachments(attachments),
        spaces: (detailValues.spaces || []),
      };
      
      const original = {
        title: actionItemDetails.actionItemTitle?.trim() || "",
        description: actionItemDetails.actionItemDescription || "",
        priority: actionItemDetails.priority || null,
        status: actionItemDetails.stage || null,
        assignee: actionItemDetails?.assignee || null,
        coach: actionItemDetails.coachProfileOrgId || null,
        sponsor: actionItemDetails?.sponsorProfileOrgId || null,
        account: actionItemDetails.clientName || actionItemDetails.client || null, // server value
        dueDate: normalizeDate(actionItemDetails.dueDate),
        subscribers: (actionItemDetails.actionItemSubscribers || [])
          .map((s: any) => s?.porId?.toLowerCase())
          .filter(Boolean)
          .sort(),
        tags: (actionItemDetails.tags || [])
          .map((t: any) => t.id ?? t)
          .sort(),
          attachments: normalizeAttachments(actionItemDetails?.attachments || []),       
          spaces: (actionItemDetails.subscribedUserGroups || []).map((s: any) => s.id),
        };
      const leftSectionCurrent = {
        title: detailValues.actionItemTitle?.trim() || "",
        description: detailValues.actionItemDescription || "",
        attachments: normalizeAttachments(attachments),
      };
      
      const leftSectionOriginal = {
        title: actionItemDetails.actionItemTitle?.trim() || "",
        description: actionItemDetails.actionItemDescription || "",
        attachments: normalizeAttachments(actionItemDetails?.attachments || []),
      };
      
      const hasLeftChanges = JSON.stringify(leftSectionCurrent) !== JSON.stringify(leftSectionOriginal);
      const hasRightChanges = JSON.stringify(current) !== JSON.stringify(original);
      
      setHasLeftSectionChanges(hasLeftChanges);
      setHasRightSectionChanges(hasRightChanges);
      
      // Existing items: Save is for real edits. isSupportMode must not keep header Save enabled
      // with no left-section changes (that path no-ops in handleSubmit).
      const isValidForSave = isNewActionItem || hasLeftChanges || hasRightChanges;
      valid = allRequiredFieldsPresent && isValidForSave;
    }
    onValidityChange?.(valid);
  }, [detailValues, actionItemDetails, isNewActionItem, openDialog, attachments, bulkTasks, bulkTaskTitles, activeUsers, isActiveUsersReady]);
  

  useEffect(() => {
    if (!actionItemDetails) return;
  
    setDetailValues({
      priority: actionItemDetails.priority ?? null,
      status:
        (actionItemDetails.stage as string) ??
        "to_do",
      assignee: 
        (actionItemDetails as any).assignee??
        (actionItemDetails as any).assigneeProfileOrdId ?? 
        null,
      coach:
        (actionItemDetails as any).coach ??
        (actionItemDetails as any).coachProfileOrgId ??
        null,
      sponsor:
        (actionItemDetails as any).sponsor ??
        (actionItemDetails as any).sponsorProfileOrgId ??
        null,
      dueDate: actionItemDetails.dueDate
        ? actionItemDetails.dueDate
        : null,
      actionItemTitle:
        actionItemDetails.actionItemTitle ??
        "",
      actionItemDescription:
        actionItemDetails.actionItemDescription ??
        "",
      subscribers: (actionItemDetails.actionItemSubscribers ?? [])
        .map((s: any) => extractPorId(s))
        .filter((id): id is string => !!id),
      deleteSubscribers: actionItemDetails.deleteSubscribers ?? [],
      tags: Array.isArray(actionItemDetails.tags)
        ? actionItemDetails.tags.map((t: any) => t.id ?? t)
        : [],
      spaces: (actionItemDetails.subscribedUserGroups ?? []).map((g: any) => g.id),
      client: {
        orgId: actionItemDetails.clientOrgId ?? null,
        name: actionItemDetails.clientName ?? null
      },
      subscribersTouched: true,
    });
    setAttachments(
      (actionItemDetails?.attachments || []).reduce(
        (acc: { [key: string]: Attachment }, attachment: any) => {
          // Use a stable identifier: prefer server ID, then path (for files), then URL, fallback to generated UUID
          // This ensures attachments keep the same UUID across refetches
          const id = (
            attachment?.id?.toString() ??
            attachment?.idString?.toString() ??
            attachment?.uuid?.toString() ??
            attachment?.path ??
            attachment?.url ??
            uuid()
          );
    
          // normalize likely field names so downstream code can rely on `url` & `description`
          const url = (attachment?.url ?? attachment?.link ?? attachment?.fileUrl ?? "").toString();
          const description = (attachment?.description ?? attachment?.name ?? "").toString();
    
          acc[id] = {   
            // preserve original fields but ensure id/url/description are present
            ...attachment,
            id,
            url,
            description,
            isFromApi: true,
          } as Attachment;
    
          return acc;
        },
        {}
      )
    );
  }, [actionItemDetails]);

  useEffect(() => {
    if (!openDialog) return;

    const onScreenId = viewedActionItemId ?? selectedActionItem?.id ?? null;
    const detailsMatchOnScreen =
      !!actionItemDetails?.actionItemId &&
      !!onScreenId &&
      actionItemDetails.actionItemId === onScreenId;
    const viewingNestedSubtask =
      !!viewedActionItemId && viewedActionItemId !== selectedActionItem?.id;

    // Row snapshots (refetch / groupBy) must not overwrite API-hydrated fields, or a child's
    // form with the table parent's selectedActionItem. New / support create still hydrates here.
    if (!isNewActionItem && (detailsMatchOnScreen || viewingNestedSubtask)) {
      return;
    }

    setDetailValues({
      priority: selectedActionItem?.priority ?? null,
      status: selectedActionItem?.status ?? groupByValues?.groupByStatus ?? "to_do",
      assignee: selectedActionItem?.assignee ?? groupByValues?.groupByAssigneeId ?? null,
      coach: selectedActionItem?.coach ?? null,
      sponsor: selectedActionItem?.sponsor ?? null,
      dueDate: selectedActionItem?.dueDate ?? null,
      actionItemTitle: selectedActionItem?.description ?? "",
      actionItemDescription: selectedActionItem?.actionItemDescription ?? "",
      subscribers: (selectedActionItem?.subscribers ?? [])
        .map((s: any) => extractPorId(s))
        .filter((id: any): id is string => !!id),
      deleteSubscribers: selectedActionItem?.deleteSubscribers ?? [],
      tags: Array.isArray(selectedActionItem?.tags)
        ? selectedActionItem.tags.map((t: any) => t.id ?? t)
        : [],
      spaces: (selectedActionItem?.spaces || []),
      client: {
        orgId: selectedActionItem?.clientOrgId ?? null,
        name: selectedActionItem?.clientName ?? null,
      },
      subscribersTouched: true,
    });
  }, [
    openDialog,
    selectedActionItem,
    groupByValues,
    isNewActionItem,
    actionItemDetails?.actionItemId,
    viewedActionItemId,
  ]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as HTMLElement;

      const isInsideEditor =
        editorRef.current?.contains(target);

      // ✅ detect tippy mention popup
      const isInsideMention =
        target.closest(".tippy-box[data-theme~='mention']");

      // ✅ optional safety (popup exists)
      const isMentionOpen = !!document.querySelector(
        ".tippy-box[data-theme~='mention']"
      );

      if (!isInsideEditor && !isInsideMention && !isMentionOpen) {
        const html = detailValues.actionItemDescription ?? "";
        const text =
          new DOMParser().parseFromString(html, "text/html").body.textContent || "";

        // hide editor if there is text OR if empty but not typing
        if (text.trim() !== "") {
          setShowEditor(false);
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [detailValues.actionItemDescription]);   

  useEffect(() => {
    if(!openDialog){
      setDetailValues(EMPTY_DETAIL_VALUES)
      setActionItemNotes([]);
      setActionItemDetails(null);
      setNewActionItemNote("");
      setFormData((prev) => ({ ...prev, dueDate: null }));
      setAttachments({});
      setSelectedTab("comments");
    }
  }, [openDialog]);

  
  const [_formData, setFormData] = useState<FormData>({
    actionName: "",
    dueDate: null,
  });
  const [errors, setErrors] = useState({
    assignee: false,
    dueDate: false,
    status: false,
    actionItemTitle: false,
    priority: false,

  });

  useEffect(() => {
    if (attendees && Array.isArray(attendees)) {
      const formattedAttendeeDetails = (attendees as Attendee_details[]).map(
        (attendee) => ({
          email: attendee?.email,
          name: attendee?.name || "",
        })
      );

      setAttendees(formattedAttendeeDetails);
    }
  }, [attendees]);

  const [_attendeesList, setAttendeesList] = useState(
    attendees?.map((obj: any) => {
      return { label: obj, value: obj };
    })
  );
  const [_personResponsible, setPersonResponsible] = useState<string | null>(
    isRhythm ? loggedInEmail : null
  );
  const [actionItemNotes, setActionItemNotes] = useState<any[]>([]);
  const [selectedNoteId, setSelectedNoteId] = useState("");
  const [newActionItemNote, setNewActionItemNote] = useState<
    string | undefined
  >(undefined);
  const [newActionItemId, setNewActionItemId] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);

  const statusList = [
    ...Object.keys(ActionItemStatus).map((key: any) => ({
      label: ActionItemStatus[key as keyof typeof ActionItemStatus],
      value: key,
      isDisabled: false,
    })),
    ...(selectedActionItem?.is_ai_generated === true
      ? [
          {
            label: "Draft",
            value: "draft",
            isDisabled: false,
          },
        ]
      : []),
  ];

  const [actionItem, setActionItem] = useState(selectedActionItem || "");

  const detailsActionItemId = isNewActionItem
    ? null
    : viewedActionItemId || (typeof actionItem === "object" ? actionItem?.id : null) || selectedActionItem?.id;
  const actionItemRecord =
    typeof actionItem === "object" && actionItem ? actionItem : null;
  const actionItemMatchesCurrent =
    !!actionItemRecord &&
    (!detailsActionItemId || actionItemRecord.id === detailsActionItemId);
  const viewingSelectedItem =
    !detailsActionItemId || detailsActionItemId === selectedActionItem?.id;
  const sourceItem = actionItemMatchesCurrent
    ? actionItemRecord
    : viewingSelectedItem
      ? selectedActionItem
      : null;
  const isSubTaskParam =
    resolveIsSubTaskFlag(sourceItem) || resolveIsSubTaskFlag(actionItemDetails);
  const isCurrentlySubtask = isSubTaskParam;

  const {
    isSuccess: isActionItemDetailsSuccess,
    isLoading: isActionItemDetailsLoading,
    isFetching: isActionItemDetailsFetching,
    isError: isActionItemDetailsError,
    error: ActionItemDetailsError,
    data: ActionItemDetailsData,
    refetch,
  } = ActionItemService.useGetActionItemDetails(
    {
      actionItemId: detailsActionItemId,
      isSubTask: isSubTaskParam,
    }, 
    Boolean(openDialog && detailsActionItemId)
  );

  useEffect(() => {
    setShowSpinner(isActionItemDetailsLoading || isActionItemDetailsFetching);
    if (!openDialog || isNewActionItem || !detailsActionItemId) return;
    if (isActionItemDetailsSuccess) {
      // ActionItemDetailsData is likely the react-query result -> axios response
      // If your query returns the axios response, `ActionItemDetailsData?.data` is the payload.
      // If your query already returns response.data, then use `ActionItemDetailsData` directly.
      const payload = ActionItemDetailsData?.data ?? ActionItemDetailsData;
  
      if (payload) {
        const payloadId = payload.actionItemId ?? payload.id;
        if (payloadId && payloadId !== detailsActionItemId) return;
        // Optional: normalize/parse dates (uncomment if you want Date objects)
        const normalized: ActionItemDetailsType = {
          ...payload,
          actionItemId: payload.actionItemId ?? "", // Ensure actionItemId is included
          dueDate: payload.dueDate ?? null,
          revisedDueDate: payload.revisedDueDate ? new Date(payload.revisedDueDate) : null,
        };
  
        setActionItemDetails(normalized);
        if (!viewedActionItemId || viewedActionItemId === selectedActionItem?.id) {
          setActionItemRowDetails?.(normalized);
        }
      } else {
        // clear state if payload missing
        setActionItemDetails(null);
      }
    }
    if (isActionItemDetailsError) {
      const errorMessage =
        ActionItemDetailsError?.response?.data?.errors || "An unknown error occurred";
      showSnackbar("error", errorMessage);
    }
  }, [
    isActionItemDetailsLoading,
    isActionItemDetailsFetching,
    isActionItemDetailsSuccess,
    isActionItemDetailsError,
    ActionItemDetailsError,
    ActionItemDetailsData,
    viewedActionItemId,
    selectedActionItem?.id,
    openDialog,
    isNewActionItem,
    detailsActionItemId,
  ]);

  const shouldFetchTags = !tagsList || tagsList.length === 0;
  const shouldFetchSpaces = !spacesList || spacesList.length === 0;

    const {
      isSuccess: isTagSuccess,
      isError: isTagsError,
      error: tagsError,
      data: TagsData,
    } = ActionItemService.useGetActionItemTags(shouldFetchTags);

    const {
      isSuccess: isSpaceSuccess,
      isError: isSpacesError,
      error: spacesError,
      data: spacesData,
    } = ActionItemService.useGetSpaces(shouldFetchSpaces);

  const {
    isSuccess,
    isLoading: isActiveUsersLoading,
    isFetching: isActiveUsersFetching,
    isError,
    error,
    data: activeUsersData,
  } = UserInteractionsService.useGetActiveUsers( {}, true );

  useEffect(() => {
    if(isSuccess) {
      const activeUsers = activeUsersData?.data?.data || [];
      setActiveUsers(activeUsers);
      setIsActiveUsersReady(true);
    }
    if (isError) {
      setIsActiveUsersReady(true);
      showSnackbar("error", `Error fetching active users: ${error}`);
    }
  }, [isError, error, activeUsersData, isSuccess, isActiveUsersLoading, isActiveUsersFetching]);

  const {
    isError: isAccountsError,
    error: accountsError,
    data: AccountsData,
} = ClientService.useGetActiveClients(true);

useEffect(() => {
        if (AccountsData) {
        const AccountsList = AccountsData?.data || [];
        setClientList(AccountsList);

        } else if (isAccountsError) {
        showSnackbar("error", accountsError?.message || "Failed to load data.");
        }   
    }, [AccountsData, isAccountsError, accountsError]);

    useEffect(() => {
      if (Array.isArray(tagsList) && tagsList.length > 0) {
        setLocalTagsList(tagsList);
      }
    }, [tagsList]);

    useEffect(() => {
      if(isTagSuccess){
      if (shouldFetchTags && TagsData?.data) {
        const fetchedTags = TagsData?.data || [];
        setLocalTagsList(fetchedTags);
      }
    } else if (isTagsError){
      showSnackbar("error", tagsError.message)
    }
    }, [shouldFetchTags, TagsData, isTagSuccess, isTagsError]);
    
    useEffect(() => {
      if(isSpaceSuccess){
        if (shouldFetchSpaces && spacesData?.data) {
          const fetchedSpaces = spacesData?.data || [];
          setLocalSpacesList(fetchedSpaces)
        }
      } else if(isSpacesError){
        showSnackbar("error", spacesError.message)
      }
    }, [shouldFetchSpaces, spacesData, isSpacesError, isSpaceSuccess]);

  const isImpersonating = impersonationDetails?.isImpersonating === true;
    
  const canEdit = (actionItemDetails?.canEdit ?? selectedActionItem?.canEdit) && !isImpersonating;
  const detailSubtasks = normalizeSubtasks(actionItemDetails);
  const subtasks = (actionItemDetails || viewedActionItemId)
    ? detailSubtasks
    : normalizeSubtasks(selectedActionItem);

  const ancestorCrumbs = isCurrentlySubtask
    ? (breadcrumbTrail.length
        ? breadcrumbTrail
        : getAncestorBreadcrumbs(actionItemDetails, selectedActionItem))
    : [];
  const isTask = Boolean(
    actionItemDetails?.isTask ??
    (typeof actionItem === "object" ? (actionItem as any)?.isTask : undefined) ??
    selectedActionItem?.isTask ??
    false
  );

  const canAddSubtask = Boolean(
    canEdit &&
    !isNewActionItem &&
    detailsActionItemId &&
    !isCurrentlySubtask &&
    isTask === true
  );
  const [status, setStatus] = useState<Option | null>(null);

  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const lastManualUpdatePayloadRef = useRef<Record<string, any> | null>(null);

  const [hideMenu, setHideMenu] = useState(true);

  const { mutateAsync: addAttachmentMutation } = ActionItemService.useAddAttachment({});

  const { mutate: mutateActionItemCreate } =
    ActionItemService.usePostCreateActionItem(meetingId || groupByValues?.groupByInteractionId || "", {
      onMutate: () => {
        setShowSpinner(true);
      },
      onSuccess: async (data: any) => {
        const newId = data?.data?.actionItemId;
        const createdSubtask = createAsSubtaskRef.current;
        if (createdSubtask) {
          createAsSubtaskRef.current = false;
        } else {
          setNewActionItemId(newId || "");
        }
        if (!createdSubtask && newId && pendingAttachments.length > 0) {
          try {
            for (const file of pendingAttachments) {
              await addAttachmentMutation({
                actionItemId: newId,
                file,
              });
            }
            setPendingAttachments([]);
          } catch (e) {
            showSnackbar("error", "Some attachments failed to upload");
          }
        }
        onUnsavedChangesChange?.(false);
        setHasLeftSectionChanges(false);
        setHasRightSectionChanges(false);
        queryClient.invalidateQueries({ queryKey: ["get_user_action_items"] });
        actionItemType === "MEETING_ACTION_ITEMS" &&
          queryClient.invalidateQueries({ queryKey: ["get_action_items"] });
        actionItemType === "MY_ACTION_ITEMS" &&
          queryClient.invalidateQueries({
            queryKey: ["get_my_action_items"],
          });
          if(groupByValues) {
            queryClient.invalidateQueries({
              queryKey: ["get_group_by_action_items"],
            });
          }
          queryClient.invalidateQueries({
            queryKey: ["get_action_item_count"],
          });
        actionItemType === "TEAM_ACTION_ITEMS" &&
          queryClient.invalidateQueries({
            queryKey: ["get_my_action_items"],
          });
          actionItemType === "SUBSCRIBED_ACTION_ITEMS" && 
          queryClient.invalidateQueries({
            queryKey: ["get_my_action_items"],
          });
          queryClient.invalidateQueries({
            queryKey: ["get_action_item_details"],
          });
          if (createdSubtask) {
            setIsAddingSubtask(false);
            setNewSubtaskTitle("");
            setNewSubtaskAssignee(null);
            setNewSubtaskDueDate(null);
            setNewSubtaskStatus("to_do");
            refetch?.();
            showSnackbar("success", "Task created successfully");
            return;
          }
          setDetailValues(EMPTY_DETAIL_VALUES);
        if (!newActionItemNote) {
          handleCancel();
          showSnackbar(
            "success",
            `${isRhythm ? "Rhythm" : "Task"} created successfully`
          );
        }
      },
      onError: (error: string) => {
        createAsSubtaskRef.current = false;
        showSnackbar(
          "error",
          `Error while creating ${
            isRhythm ? "rhythm" : "task"
          }: ${error}`
        );
      },
      onSettled: () => {
        setShowSpinner(false);
      },
    });

    useEffect(() => {
      onUnsavedChangesChange?.(hasLeftSectionChanges);
    }, [hasLeftSectionChanges]);

    useEffect(() => {
      if (openDialog && isNewActionItem) {
        setDetailValues(prev => ({
          ...prev,
          priority: "medium"
        }));
      }
    }, [openDialog, isNewActionItem]);    

  const applyManualPayloadToLocalState = (payload: Record<string, any>) => {
    setActionItemDetails((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        ...(payload.title !== undefined && { actionItemTitle: payload.title }),
        ...(payload.description !== undefined && { actionItemDescription: payload.description }),
        ...(payload.dueDate !== undefined && { dueDate: payload.dueDate ?? undefined }),
        ...(payload.priority !== undefined && { priority: payload.priority ?? null }),
        ...(payload.status !== undefined && { stage: payload.status }),
        ...(payload.interactionId !== undefined && { interactionId: payload.interactionId ?? null }),
        ...(payload.assigneePorId !== undefined && { assignee: payload.assigneePorId ?? null }),
        ...(payload.coachPorId !== undefined && { coachProfileOrgId: payload.coachPorId ?? null }),
        ...(payload.sponsorPorId !== undefined && { sponsorProfileOrgId: payload.sponsorPorId ?? null }),
        ...(payload.clientOrgId !== undefined && { clientOrgId: payload.clientOrgId ?? null }),
        ...(payload.tagIds !== undefined && { tags: payload.tagIds ?? [] }),
        ...(payload.attachments !== undefined && { attachments: payload.attachments ?? [] }),
      };
    });
  };

  const triggerManualUpdate = (payload: Record<string, any>) => {
    lastManualUpdatePayloadRef.current = payload;
    mutateActionItemUpdate(payload);
  };

  const invalidateActionItemQueries = () => {
    actionItemType === "MEETING_ACTION_ITEMS" &&
      queryClient.invalidateQueries({
        queryKey: ["get_action_items"],
      });
    actionItemType === "MY_ACTION_ITEMS" &&
      queryClient.invalidateQueries({
        queryKey: ["get_my_action_items"],
      });
    actionItemType === "TEAM_ACTION_ITEMS" &&
      queryClient.invalidateQueries({
        queryKey: ["get_my_action_items"],
      });
    actionItemType === "SUBSCRIBED_ACTION_ITEMS" &&
      queryClient.invalidateQueries({
        queryKey: ["get_user_subscribed_action_items"],
      });
    if (groupByValues) {
      queryClient.invalidateQueries({
        queryKey: ["get_group_by_action_items"],
      });
    }
    queryClient.invalidateQueries({
      queryKey: ["get_action_item_count"],
    });
  };

  const actionItemUpdateHandlers = {
      onMutate: () => {
        setShowSpinner(true);
        sessionStorage.setItem("actionItemUpdated", JSON.stringify(true));
      },
      onSuccess: () => {
        if (isAutoSavingRef.current) {
          isAutoSavingRef.current = false;
          refetch?.();
          invalidateActionItemQueries();
          return;
        }
        if (lastManualUpdatePayloadRef.current) {
          applyManualPayloadToLocalState(lastManualUpdatePayloadRef.current);
          lastManualUpdatePayloadRef.current = null;
        }
        saveActionItemNote();
        onUnsavedChangesChange?.(false);
        setHasLeftSectionChanges(false);
        setHasRightSectionChanges(false);
        invalidateActionItemQueries();
        const searchPayload = sessionStorage.getItem("globalSearchPayload");
        if (searchPayload) {
          const parsedParams = JSON.parse(searchPayload);

          queryClient
            .invalidateQueries({
              queryKey: [
                "get_global_search_results",
                `${parsedParams.search_text}_${parsedParams.email_id}_${parsedParams.calendar_domain}`,
              ],
            })
            .then(() => {
              queryClient.refetchQueries({
                queryKey: [
                  "get_global_search_results",
                  `${parsedParams.search_text}_${parsedParams.email_id}_${parsedParams.calendar_domain}`,
                ],
              });
            });
        }
        const isExistingCommentsUpdated =
          actionItemNotes?.filter((note) => note.isEdited)?.length > 0 || false;

        if (!newActionItemNote && !isExistingCommentsUpdated) {
          showSnackbar(
            "success",
            `${isRhythm ? "Rhythm" : "Task"} updated successfully`
          );
        }
      },
      onError: (error: string) => {
        showSnackbar(
          "error",
          `Error while updating ${
            isRhythm ? "rhythm" : "task"
          }: ${error}`
        );
      },
      onSettled: () => {
        setShowSpinner(false);
      },
    };

  const { mutate: mutateActionItemUpdate } =
    ActionItemService.useUpdateActionItem(actionItem?.id, actionItemUpdateHandlers);

  const linkedInteractionActionItemIds = !isCurrentlySubtask && actionItem?.id
    ? [
        actionItem.id,
        ...subtasks
          .map((sub) => sub.actionItemId)
          .filter((id): id is string => !!id && id !== actionItem.id),
      ]
    : actionItem?.id;

  const { mutate: mutateLinkedInteractionUpdate } =
    ActionItemService.useUpdateActionItem(
      linkedInteractionActionItemIds,
      actionItemUpdateHandlers
    );

    useEffect(() => {
      if (openDialog && isNewActionItem) {
        setActionItem(""); // ensures CREATE path is used
        setActionItemDetails(null);
        setSelectedInteractionId(null);
        setShowLinkedInteraction(false);
      }
    }, [openDialog, isNewActionItem]);    

    useEffect(() => {
      if(saveTrigger) {
        handleSubmit("SAVE");
        resetSaveTrigger && resetSaveTrigger();
      }
      if(discardTrigger) {
        // handleCancel();
        setDetailValues(EMPTY_DETAIL_VALUES);
        setActionItemNotes([]);
        setAttachments({});
        setShowEditor(false);
        resetDiscardTrigger && resetDiscardTrigger();
      }
    },[saveTrigger, discardTrigger])
  useEffect(() => {
    setAttendeesList(
      Attendees?.map((attendee) => ({
        label: attendee.name,
        value: attendee.email,
      }))
    );
  }, [Attendees]);

  useEffect(() => {
    if (viewedActionItemId && viewedActionItemId !== selectedActionItem?.id) {
      return;
    }
    setFormData({
      actionName: selectedActionItem?.description || "",
      dueDate: selectedActionItem?.dueDate ? dayjs(selectedActionItem?.dueDate) : null,
    });
    if (selectedActionItem?.assignee)
      setPersonResponsible(selectedActionItem.assignee);

    setStatus({
      label:
        (selectedActionItem?.status &&
          statusList.filter(
            (status) => status.value === selectedActionItem?.status
          )?.[0]?.label) ||
        statusList[0].label,
      value: selectedActionItem?.status || statusList[0].value,
    });
    setAttachments(
      (selectedActionItem?.attachments?.length > 0 &&
        selectedActionItem?.attachments) ||
        []
    );
    setActionItem(selectedActionItem);

    if (selectedActionItem) {
      const userPermission =
        selectedActionItem?.creator === loggedInUserId ? "ALL" : "LIMITED";
      setUserPermission(userPermission);
    }
  }, [selectedActionItem, viewedActionItemId, loggedInUserId]);

  const currentCommentTargetId = viewedActionItemId || selectedActionItem?.id;

  const {
    isLoading: isActionNoteLoading,
    isFetching: isActionNoteFetching,
    isSuccess: isActionNoteSuccess,
    isError: isActionNoteError,
    error: ActionNoteError,
    data: ActionItemNotesData,
  } = ActionItemNotesService.useGetActionItemNote(
    currentCommentTargetId,
    Boolean(currentCommentTargetId && openDialog),
    "desc",
  );

  useEffect(() => {
    if (openDialog && currentCommentTargetId) {
      setActionItemNotes([]);
      setNewActionItemNote("");
    }
  }, [openDialog, currentCommentTargetId]);
  const { mutate: mutateActionItemNoteCreate } =
    ActionItemNotesService.useCreateActionItemNote(
      newActionItemId || currentCommentTargetId,
      {
        onMutate: () => {
          setShowSpinner(true);
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [`get_action_item_comments`],
          });
          handleCancel();
          showSnackbar(
            "success",
            `${isRhythm ? "Rhythm" : "Task"} created successfully`
          );
        },
        onError: (error: string) => {
          handleCancel();
          showSnackbar(
            "error",
            `Error while creating task note: ${error}`
          );
        },
        onSettled: () => {
          setShowSpinner(false);
        },
      }
    );

  const { mutate: mutateActionItemNoteUpdate } =
    ActionItemNotesService.useUpdateActionItemNote(
      newActionItemId || currentCommentTargetId,
      {
        onMutate: () => {
          setShowSpinner(true);
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["get_action_item_comments"],
          });
          handleCancel();
          showSnackbar(
            "success",
            `${isRhythm ? "Rhythm" : "Task"} updated successfully`
          );
          // showSnackbar("success", "Action item note updated successfully");
        },
        onError: (error: string) => {
          handleCancel();
          showSnackbar(
            "error",
            `Error while updating task note: ${error}`
          );
        },
        onSettled: () => {
          setShowSpinner(false);
        },
      }
    );

  useEffect(() => {
    if (isActionNoteSuccess) {
      const result = ActionItemNotesData?.data || [];
      const formattedActionItemNotes = result?.map((note: any) => {
        return { ...note, isEdited: false };
      });
      setActionItemNotes(formattedActionItemNotes);
    }
    if (isActionNoteError) {
      const errorMessage =
        ActionNoteError?.response?.data?.errors || "An unknown error occurred";
      showSnackbar("error", errorMessage);
    }
  }, [
    isActionNoteLoading,
    isActionNoteFetching,
    isActionNoteSuccess,
    isActionNoteError,
  ]);

  useEffect(() => {
    if(!openDialog){
      setDetailValues(EMPTY_DETAIL_VALUES)
      setActionItemNotes([]);
      setActionItemDetails(null);
      setNewActionItemNote("");
      setFormData((prev) => ({ ...prev, dueDate: null }));
      setAttachments({});
      setPendingAttachments([]);
      setSelectedTab("comments");
      setShowEditor(false);
      setHasLeftSectionChanges(false);
      setHasRightSectionChanges(false);
      setRemovedFields([]);
      setBulkTaskTitles([""]);
    }
  }, [openDialog]);

  useEffect(() => {
    if (newActionItemId && newActionItemNote) {
      mutateActionItemNoteCreate({
        comment: newActionItemNote,
        isForUpdate: false,
        commentType: null,
      });
    }
  }, [newActionItemId]);

  const reset = () => {
    setUserPermission("ALL");
    setFormData({
      actionName: "",
      dueDate: dayjs(),
    });
    setErrors({
      assignee: false,
      dueDate: false,
      status: false,
      actionItemTitle: false,
      priority: false,});
    setPersonResponsible(isRhythm ? loggedInEmail : null);
    setStatus({
      label: actionItemStatuses[0].label,
      value: actionItemStatuses[0].value,
    });
    setAttachments({});
    commentsReset();
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };
  
  const handleAttachmentInputChange = (
    uuid: string,
    field: keyof Attachment,
    value: string
  ) => {
    setAttachments((prevAttachments) => {
      const current = prevAttachments[uuid] || {};
      let newAttachment = {
        ...current,
        [field]: value,
        ...(field === "url" && { urlError: !isValidUrl(value) }),
      };
  
      // If user updated URL and description is empty → auto-generate one
      if (field === "url" && (!current.description || current.description.trim() === "")) {
        try {
          const urlObj = new URL(value);
          const pathParts = urlObj.pathname.split("/").filter(Boolean);
  
          let desc: string;
  
          if (pathParts.length > 0) {
            // use last path segment
            desc = pathParts[pathParts.length - 1];
          } else {
            // fallback → hostname without www
            desc = urlObj.hostname.replace(/^www\./, "");
          }
  
          // strip query/hash/extension
          desc = desc.split("?")[0].split("#")[0];
          desc = desc.replace(/\.[^/.]+$/, ""); 
  
          newAttachment.description = desc || urlObj.hostname.replace(/^www\./, "");
        } catch {
          // fallback if invalid URL
          newAttachment.description = value;
        }
      }
  
      return {
        ...prevAttachments,
        [uuid]: newAttachment,
      };
    });
    if (isNewActionItem || isSupportTask) {
      setHasLeftSectionChanges(true);
      onUnsavedChangesChange?.(true);
    }
  };   
  

  const handleRemoveField = (uuid: string) => {
    setAttachments((prevAttachments) => {
      const updatedAttachments = { ...prevAttachments };
      delete updatedAttachments[uuid];
      return updatedAttachments;
    });
    if (isNewActionItem || isSupportTask) {
      setHasLeftSectionChanges(true);
      onUnsavedChangesChange?.(true);
    }
  };
  const commentsReset = () => {
    setNewActionItemId("");
    setNewActionItemNote("");
    setSelectedNoteId("");
  };

  const handleCancel = () => {
    reset();
    setActionItem("");
    handleClose && handleClose();
  };

  const getISODate = (value: any): string | null => {
    if (!value) return null;
    return dayjs(value).format("YYYY-MM-DD");
  };

  const scrollTaskPanelToTop = () => {
    leftSectionRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateToActionItem = (item: {
    id: string;
    title: string;
    isSubTask?: boolean;
    parentDueDate?: string | null;
  }) => {
    setIsAddingSubtask(false);
    setNewSubtaskTitle("");
    setNewSubtaskAssignee(null);
    setNewSubtaskDueDate(null);
    setNewSubtaskStatus("to_do");
    setHasLeftSectionChanges(false);
    setHasRightSectionChanges(false);
    onUnsavedChangesChange?.(false);
    setActionItemDetails(null);
    setAttachments({});
    setDetailValues({
      ...EMPTY_DETAIL_VALUES,
      actionItemTitle: item.title,
    });
    setActionItem({
      id: item.id,
      description: item.title,
      isSubTask: item.isSubTask,
    });
    setViewedActionItemId(item.id);
    scrollTaskPanelToTop();
  };

  const handleOpenSubtask = (subtask: SubtaskItem) => {
    const currentId = actionItemDetails?.actionItemId;
    const currentTitle =
      actionItemDetails?.actionItemTitle ||
      detailValues.actionItemTitle ||
      selectedActionItem?.description ||
      "";
    const currentDueDate =
      detailValues.dueDate || actionItemDetails?.dueDate || null;
    if (currentId) {
      setBreadcrumbTrail((prev) => {
        const alreadyInTrail = prev.some((crumb) => crumb.id === currentId);
        if (alreadyInTrail) return prev;
        return [...prev, { id: currentId, title: currentTitle, dueDate: currentDueDate }];
      });
    }
    navigateToActionItem({
      id: subtask.actionItemId,
      title: subtask.title,
      isSubTask: true,
      parentDueDate: currentDueDate,
    });
    setSelectedTab("comments");
  };

  const handleBreadcrumbSelect = (crumb: ActionItemBreadcrumb, index: number) => {
    setBreadcrumbTrail((prev) => prev.slice(0, index));
    const isSub = index > 0;
    const parentDueDate = isSub ? breadcrumbTrail[index - 1]?.dueDate ?? null : null;
    navigateToActionItem({
      id: crumb.id,
      title: crumb.title,
      isSubTask: isSub,
      parentDueDate,
    });
    if (!isSub) {
      setSelectedTab("comments");
    }
  };

  const handleSaveSubtask = () => {
    const title = newSubtaskTitle.trim();
    if (!title) {
      showSnackbar("info", "Please enter a task title.");
      return;
    }
    const parentId =
      actionItemDetails?.actionItemId ||
      (typeof actionItem === "object" ? actionItem?.id : null) ||
      selectedActionItem?.id ||
      detailsActionItemId;
    if (!parentId) return;

    const dueDate = newSubtaskDueDate ? getISODate(newSubtaskDueDate) : null;
    const assignee = newSubtaskAssignee || loggedInProfileOrgId || detailValues.assignee || "";
    const actionSubscribersPorIds: string[] = [];
    if (
      typeof loggedInProfileOrgId === "string" &&
      loggedInProfileOrgId.trim() !== "" &&
      assignee !== loggedInProfileOrgId &&
      !actionSubscribersPorIds.includes(loggedInProfileOrgId)
    ) {
      actionSubscribersPorIds.push(loggedInProfileOrgId);
    }
    const parentInteractionId =
      selectedInteractionId || actionItemDetails?.interactionId || null;
    createAsSubtaskRef.current = true;
    mutateActionItemCreate(
      {
        title,
        parentActionItemId: parentId,
        ...(assignee ? { assigneePorId: assignee } : {}),
        ...(dueDate ? { dueDate } : {}),
        status: newSubtaskStatus || "to_do",
        priority: detailValues.priority || "medium",
        ...(parentInteractionId ? { interactionId: parentInteractionId } : {}),
        ...(actionSubscribersPorIds.length > 0 && { actionSubscribersPorIds }),
      },
      {
        onSuccess: () => {
          setIsAddingSubtask(false);
          setNewSubtaskTitle("");
          setNewSubtaskAssignee(null);
          setNewSubtaskDueDate(null);
          setNewSubtaskStatus("to_do");
        },
      }
    );
  };

  const handleSubmit = (type: "SAVE" | "REJECT") => {
    const hasTaskTitle = bulkTasks
      ? bulkTaskTitles.some((title) => title.trim()?.length > 0)
      : detailValues.actionItemTitle?.trim() !== "";
    const newErrors = {
      actionItemTitle: !hasTaskTitle,
      dueDate: detailValues.dueDate === null,
      status: status === null,
      assignee: detailValues.assignee === null,
      priority: detailValues.priority === null,
    };
    setErrors(newErrors);

    // Existing items persist the right pane via autosave. Header Save is for left-section
    // edits (and for create). Existing support tickets hide header buttons in TaskManagement.
    if (actionItemDetails && !isNewActionItem && !hasLeftSectionChanges) {
      return;
    }
    if (!Object.values(newErrors).some((error) => error)) {
      shouldRestoreTitleFocusRef.current = true;
      const validAttachments = normalizeAttachments(attachments);
      const isoDueDate = getISODate(detailValues.dueDate);
      const existingSubscribers: string[] =
      Array.isArray(detailValues.subscribers) ? detailValues.subscribers.filter(Boolean) : [];
      const normalizedSet = new Set<string>();
      const lowerSeen = new Set<string>();
      
      const safeAdd = (raw?: string | null) => {
        if (!raw) return;
        const s = raw.toString().trim();
        if (!s) return;
        const lower = s.toLowerCase();
        if (!lowerSeen.has(lower)) {
          lowerSeen.add(lower);
          normalizedSet.add(s);
        }
      };
      
      // seed from existing subscribers (preserve first-seen casing)
      for (const s of existingSubscribers) {
        safeAdd(s);
      }
      
      // add loggedInEmail only if present and not the assignee
      if (typeof loggedInEmail === "string" && loggedInEmail.trim() !== "" && detailValues.assignee !== loggedInEmail) {
        safeAdd(loggedInEmail);
      }
      
      // add coach & sponsor (if present)
      safeAdd(detailValues.coach ?? null);
      safeAdd(detailValues.sponsor ?? null);
      const tagIds =
      Array.isArray(detailValues.tags) && detailValues.tags.length > 0
        ? detailValues.tags.map((t: any) =>
            typeof t === "string" ? t : t.tagId ?? t.id
          )
        : [];
      
      const originalSubscribers = (selectedActionItem?.subscribers || [])
        .map((id: any) => extractPorId(id))
        .filter(Boolean);
      const currentSubscribers = [...(detailValues.subscribers || [])];
      if (
        isNewActionItem &&
        typeof loggedInProfileOrgId === "string" &&
        loggedInProfileOrgId.trim() !== "" &&
        detailValues.assignee !== loggedInProfileOrgId &&
        !currentSubscribers.includes(loggedInProfileOrgId)
      ) {
        currentSubscribers.push(loggedInProfileOrgId);
      }
      const deleteSubscribersPorIds = originalSubscribers.filter(
        (profileOrgRelationshipId: string) => !currentSubscribers.includes(profileOrgRelationshipId)
      );
      const hadOriginalTags = Array.isArray(actionItemDetails?.tags) && actionItemDetails.tags.length > 0;
      const hadOriginalAttachments = Array.isArray(actionItemDetails?.attachments) && actionItemDetails.attachments.length > 0;

      const currentSpaces = detailValues.spaces || [];
      const originalSpaces = actionItemDetails?.subscribedUserGroups?.map((g: any) => g.id) || [];

      const addSpaces = currentSpaces.filter((s) => !originalSpaces.includes(s));
      const deleteSpaces = originalSpaces.filter((s) => !currentSpaces.includes(s));

      const currentDescriptionHtml = descriptionEditorRef.current?.getHTML?.() || detailValues.actionItemDescription || "";

      const userData: ActionItemData = {
        ...(currentDescriptionHtml && {
          description: currentDescriptionHtml,
        }),
      
        ...(detailValues.actionItemTitle?.trim() && {
          title: detailValues.actionItemTitle.trim(),
        }),
      
        ...(detailValues.coach && {
          coachPorId: detailValues.coach,
        }),
      
        ...(detailValues.priority && {
          priority: detailValues.priority,
        }),
      
        ...(detailValues.assignee && {
          assigneePorId: detailValues.assignee,
        }),
        ...(detailValues.status && {
        status: detailValues.status as "to_do" | "in_progress" | "done" ?? "to_do",
        }),
        ...(validAttachments.length > 0
          ? { attachments: validAttachments }
          : hadOriginalAttachments
          ? { attachments: [] }
          : {}),
        ...(currentSubscribers.length > 0 && {
          actionSubscribersPorIds: currentSubscribers,
        }),
        ...(isoDueDate && {
          dueDate: isoDueDate,
        }),
        ...(detailValues.sponsor && {
          sponsorPorId: detailValues.sponsor,
        }),
        ...((actionItem?.id || selectedActionItem?.id) && {
          actionItemId: actionItem?.id || selectedActionItem?.id,
        }),
        ...(deleteSubscribersPorIds.length > 0 && {
          deleteSubscribersPorIds,
        }),
        ...(tagIds.length > 0
          ? { tagIds }
          : hadOriginalTags
          ? { tagIds: [] }
          : {}),
      
        ...(addSpaces.length > 0 && {
          addSpaces,
        }),
      
        ...(deleteSpaces.length > 0 && {
          deleteSpaces,
        }),
        ...((detailValues.client?.orgId ||
          groupByValues?.groupByClientDomain?.orgId) && {
          clientOrgId:
            detailValues.client?.orgId ||
            groupByValues?.groupByClientDomain?.orgId,
        }),
      
        ...(removedFields?.length > 0 && {
          removeFields: removedFields,
        }),
        ...(isSupportTask && {
          isSupportTicket: true,
        }),
        interactionId: selectedInteractionId ?? null,
        ...(!bulkTasks && (selectedActionItem?.parentActionItemId || actionItemDetails?.actionItemId) && createAsSubtaskRef.current && {
          parentActionItemId: selectedActionItem?.parentActionItemId || actionItemDetails?.actionItemId,
        }),
        ...(!bulkTasks && selectedActionItem?.parentActionItemId && isNewActionItem && {
          parentActionItemId: selectedActionItem.parentActionItemId,
        }),
      };

      const currentActionItemId = actionItem?.id;
      if (currentActionItemId ) {
        const updatePayload: any = {};
        const currentTitle = detailValues.actionItemTitle?.trim() || "";
        const originalTitle = actionItemDetails?.actionItemTitle?.trim() || "";
        const currentDescription = descriptionEditorRef.current?.getHTML?.() || detailValues.actionItemDescription || "";
        const originalDescription = actionItemDetails?.actionItemDescription || "";
        const currentAttachments = normalizeAttachments(attachments);
        const originalAttachments = normalizeAttachments(actionItemDetails?.attachments || []);

        if (currentTitle !== originalTitle) {
          updatePayload.title = currentTitle;
        }
        if (currentDescription !== originalDescription) {
          updatePayload.description = currentDescription;
        }
        if (JSON.stringify(currentAttachments) !== JSON.stringify(originalAttachments)) {
          updatePayload.attachments = currentAttachments;
        }

        if (Object.keys(updatePayload).length === 0) return;
        triggerManualUpdate(updatePayload);
        type === "REJECT" && handleCancel();
      } else if (bulkTasks) {
        const titles = bulkTaskTitles.map((title) => title.trim()).filter(Boolean);
        if (titles?.length === 0) return;

        mutateActionItemCreate({
          ...userData,
          titles,
          assigneePorId: userData?.assigneePorId ?? "",
          priority: userData?.priority ?? "",
        });
      } else {
        mutateActionItemCreate({
          ...userData,
          assigneePorId: userData.assigneePorId ?? "", // Ensure assignee is a string
          priority: userData.priority ?? "", // Ensure priority is a string
        });
      }
    }
  };

  const saveActionItemNote = () => {
    // create
    if (newActionItemNote) {
      mutateActionItemNoteCreate({
        comment: newActionItemNote,
        isForUpdate: false,
        commentType: null,
      });
    }
    // update
    if (actionItemNotes?.length > 0) {
      actionItemNotes.forEach((note: any) => {
        if (note.isEdited) {
          setSelectedNoteId(note.id);
          mutateActionItemNoteUpdate({
            actionItemId: newActionItemId || actionItem?.id,
            commentId: note.id || selectedNoteId,
            comment: note.notes,
            isForUpdate: true,
          });
        }
      });
    }
  };

  useEffect(() => {
    if (!openDialog || !isSupportTask || !isNewActionItem) return;
  
    const defaults = loggedInUserDomain ? SUPPORT_TASK_DEFAULTS[loggedInUserDomain] : null;
    if (!defaults) return;

    const matchedDefaultUser = findUserInActiveUsers(activeUsers, defaults.assignee);
    const validAssignee = matchedDefaultUser
      ? (matchedDefaultUser.profileOrgRelationshipId || matchedDefaultUser.profileOrgId || matchedDefaultUser.id || defaults.assignee)
      : null;
  
    setDetailValues((prev) => {
      // prevent overriding if already set
      if (prev.assignee || prev.subscribers?.length || prev.tags?.length || prev.dueDate) {
        return prev;
      }
      const dueDate = dayjs()
      .add(defaults.dueInDays, "day")
      .format("YYYY-MM-DD");
  
      return {
        ...prev,
        assignee: validAssignee,
        subscribers: defaults.subscribers,
        tags: [defaults.tagId],
        dueDate,
      };
    });
  }, [openDialog, isSupportTask, isNewActionItem, loggedInUserDomain, activeUsers]);

    const handleAttachmentAdd = () => {
      const key = uuid();
      setAttachments((prev) => ({ ...prev, [key]: { description: "", url: "" } }));
      if (isNewActionItem || isSupportTask) {
        setHasLeftSectionChanges(true);
        onUnsavedChangesChange?.(true);
      }
    };
    useEffect(() => {
      if (!openDialog || !selectedActionItem?.id) return;

      const mappedSubscribers = (selectedActionItem.actionItemSubscribers ?? []).map((s: { subscriberEmail: any; subScriberName: any; subscriberPictureUrl: any; porId: any }) => ({
        email: s.subscriberEmail,
        name: s.subScriberName,
        pictureUrl: s.subscriberPictureUrl,
        profileOrgId: s.porId,
      }));

      setActiveUsers((prev) => {
        const merged = [...prev];
        mappedSubscribers.forEach((sub: { profileOrgId: any }) => {
          if (!merged.find((u) => u.profileOrgId === sub.profileOrgId)) {
            merged.push(sub);
          }
        });
        return merged;
      });

      setDetailValues((prev) => ({
        ...prev,
        subscribers: mappedSubscribers.map((s: { profileOrgId: any; }) => s.profileOrgId),
        actionItemTitle: selectedActionItem.actionItemTitle,
        actionItemDescription: selectedActionItem.actionItemDescription,
        dueDate: selectedActionItem.dueDate,
        priority: selectedActionItem.priority,
        stage: selectedActionItem.stage,
        coach: selectedActionItem.coach,
        assignee: selectedActionItem.assignee,
      }));
    }, [openDialog, selectedActionItem?.id]);

    useEffect(() => {
      if (openDialog) {
        setErrors({
          assignee: false,
          dueDate: false,
          status: false,
          actionItemTitle: false,
          priority: false,
        });
      }
    }, [openDialog]);

    useEffect(() => {
      if (!openDialog) return;

      if (isNewActionItem) {
        setDetailValues((prev) => {
          const updated = { ...prev };
          let changed = false;

          // Set due date = tomorrow if empty
          if (!prev.dueDate) {
            updated.dueDate = dayjs().add(1, "day").format("YYYY-MM-DD");
            changed = true;
          }

          // Set assignee = loggedInEmail if empty
          if (!prev.assignee) {
            updated.assignee = loggedInProfileOrgId;
            changed = true;
          }

          return changed ? updated : prev;
        });
      }
    }, [openDialog, isNewActionItem, loggedInProfileOrgId]);

    const [openDeletePopup, setOpenDeletePopup] = useState(false);

    const { mutate: mutateActionItemDelete } =
      ActionItemService.useDeleteActionItem(
        actionItemDetails?.actionItemId ? [actionItemDetails.actionItemId] : [],
        {
          onMutate: () => {
            setShowSpinner?.(true);
          },
          onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ["get_my_action_items"],
              });
              queryClient.invalidateQueries({
                queryKey: ["get_action_item_count"],
              });
            showSnackbar("success", "Task deleted successfully.");
            handleCancel();
          },
          onError: (error: any) => {
            showSnackbar("error", error?.response?.data?.message || "Error deleting task.");
          },
          onSettled: () => {
            setShowSpinner?.(false);
          },
        }
      );
    const handleDelete = () => {
      setOpenDeletePopup(true);
    };
    const handleDeleteSuccess = () => {
      mutateActionItemDelete({ actionItemId: actionItemDetails?.actionItemId ? [actionItemDetails.actionItemId] : [], });
      setOpenDeletePopup(false);
    };

  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);

  type AutoSaveValue = string | string[] | null;
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingAutoSavePayloadRef = useRef<Record<string, any>>({});

  const buildAutoSavePayload = (
    key: string,
    value: AutoSaveValue,
    values: DetailValues,
    fieldsToRemove: string[] = removedFields
  ) => {
    const originalSubscribers = (actionItemDetails?.actionItemSubscribers || [])
      .map((id: any) => extractPorId(id))
      .filter((id): id is string => !!id);
    const originalSpaces = actionItemDetails?.subscribedUserGroups?.map((g: any) => g.id) || [];

    if (key === "subscribers") {
      const currentSubscribers = Array.isArray(value)
        ? value
        : values.subscribers || [];
      const deleteSubscribersPorIds = originalSubscribers.filter(
        (id) => !currentSubscribers.includes(id)
      );

      return {
        actionSubscribersPorIds: currentSubscribers,
        ...(deleteSubscribersPorIds.length > 0 && { deleteSubscribersPorIds }),
      };
    }

    if (key === "spaces") {
      const currentSpaces = Array.isArray(value) ? value : values.spaces || [];
      const addSpaces = currentSpaces.filter((id) => !originalSpaces.includes(id));
      const deleteSpaces = originalSpaces.filter((id) => !currentSpaces.includes(id));

      return {
        ...(addSpaces.length > 0 && { addSpaces }),
        ...(deleteSpaces.length > 0 && { deleteSpaces }),
      };
    }

    if (key === "tags") {
      return {
        tagIds: Array.isArray(value) ? value : [],
      };
    }

    if (key === "assignee") {
      return { assigneePorId: typeof value === "string" ? value : undefined };
    }

    if (key === "clientOrgId") {
      return { clientOrgId: typeof value === "string" ? value : null };
    }

    if (key === "coach") {
      return {
        coachPorId: typeof value === "string" ? value : null,
        removeFields: fieldsToRemove,
      };
    }

    if (key === "sponsor") {
      return {
        sponsorPorId: typeof value === "string" ? value : null,
        removeFields: fieldsToRemove,
      };
    }

    if (key === "dueDate") {
      const payload: Record<string, any> = {
        dueDate: typeof value === "string" ? value : null,
      };
      if (
        !isCurrentlySubtask &&
        typeof value === "string" &&
        Array.isArray(actionItemDetails?.subTasks) &&
        actionItemDetails.subTasks.length > 0
      ) {
        const subTasks = collectClampedSubTaskDueDates(
          { subTasks: actionItemDetails.subTasks },
          value
        );
        if (subTasks.length > 0) {
          payload.subTasks = subTasks;
        }
      }
      return payload;
    }

    if (key === "priority") {
      return { priority: typeof value === "string" ? value : null };
    }

    if (key === "status") {
      return { status: typeof value === "string" ? value : undefined };
    }

    return {};
  };

  const onAutoSave = (
    key: string,
    value: AutoSaveValue,
    fieldsToRemove: string[] = removedFields
  ) => {
    if (!canEdit && !isNewActionItem && !isSupportTask) return;
    if (isNewActionItem || isSupportTask) {
      setHasRightSectionChanges(true);
      onUnsavedChangesChange?.(true);
      return;
    }
    if (!actionItem?.id) return;
    if (!actionItemDetails?.actionItemId) return;
    if (actionItemDetails.actionItemId !== actionItem.id) return;

    let nextDetailValues: DetailValues = detailValues;

    if (key === "clientOrgId") {
      nextDetailValues = {
        ...detailValues,
        client: {
          ...detailValues.client,
          orgId: typeof value === "string" ? value : null,
        },
      };
    } else if (key === "subscribers" || key === "spaces" || key === "tags") {
      nextDetailValues = {
        ...detailValues,
        [key]: Array.isArray(value) ? value : [],
      } as DetailValues;
    } else {
      nextDetailValues = {
        ...detailValues,
        [key]: value,
      } as DetailValues;
    }

    const payloadChunk = buildAutoSavePayload(key, value, nextDetailValues, fieldsToRemove);
    const stableInteractionId =
      selectedInteractionId ?? actionItemDetails?.interactionId ?? null;
    if (stableInteractionId) {
      (payloadChunk as Record<string, any>).interactionId = stableInteractionId;
    }
    if (Object.keys(payloadChunk).length === 0) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    pendingAutoSavePayloadRef.current = {
      ...pendingAutoSavePayloadRef.current,
      ...payloadChunk,
    };

    saveTimeoutRef.current = setTimeout(() => {
      const payloadToSave = pendingAutoSavePayloadRef.current;
      if (Object.keys(payloadToSave).length === 0) return;
      isAutoSavingRef.current = true;
      mutateActionItemUpdate(payloadToSave as any);
      pendingAutoSavePayloadRef.current = {};
      setHasLeftSectionChanges(false);
      setHasRightSectionChanges(false);
      onUnsavedChangesChange?.(false);
    }, 800);
  };

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      pendingAutoSavePayloadRef.current = {};
    };
  }, []);

  const handleSaveTitle = () => {
    if (!detailValues.actionItemTitle?.trim()) return;
    shouldRestoreTitleFocusRef.current = true;
    const titlePayload = {
      ...(detailValues.actionItemTitle?.trim() && {
        title: detailValues.actionItemTitle.trim(),
      }),
    };
    if (actionItem?.id) {
      triggerManualUpdate({
        ...titlePayload,
      });
    }
  };

  const isTitleEditing = detailValues.actionItemTitle?.trim() !== actionItemDetails?.actionItemTitle?.trim();

  const saveUrlAttachmentsOnly = async (updatedAttachments: {
    [key: string]: Attachment;
  }) => {
    if (!actionItem?.id && !selectedActionItem?.id) return;

    const validAttachments = normalizeAttachments(updatedAttachments);

    triggerManualUpdate({
      actionItemId: actionItem?.id || selectedActionItem?.id,
      attachments: validAttachments,
    });
  };

  const isDescriptionChanged = 
  (detailValues.actionItemDescription?.trim() || "") !== 
  (actionItemDetails?.actionItemDescription?.trim() || "");
  const canEditRightSection = canEdit || isNewActionItem || isSupportTask;

  const handleInteractionSelection = (
    id: string | null | undefined,
    details?: {
      interactionTitle?: string | null;
      startTime?: string | null;
      duration?: number | null;
      isLinkInteractionDelete?: boolean;
    }
  ) => {
    const nextId = id || null;
    setSelectedInteractionId(nextId);

    setActionItemDetails((prev) => {
      if (!prev) return prev;
      if (nextId === null) {
        return {
          ...prev,
          interactionId: null,
          interactionTitle: null,
          interactionStartTime: null,
          interactionDuration: null,
        };
      }
      if (!details) {
        return {
          ...prev,
          interactionId: nextId,
        };
      }
      return {
        ...prev,
        interactionId: nextId,
        interactionTitle: details.interactionTitle ?? prev.interactionTitle ?? null,
        interactionStartTime: details.startTime ?? prev.interactionStartTime ?? null,
        interactionDuration: details.duration ?? prev.interactionDuration ?? null,
      };
    });

    if (!actionItem?.id) return;
    const originalInteractionId = actionItemDetails?.interactionId ?? null;
    if (nextId === originalInteractionId) return;
    const isDelete = nextId === null || details?.isLinkInteractionDelete === true;
    const payload = {
      interactionId: nextId,
      ...(isDelete && { isLinkInteractionDelete: true }),
    };
    lastManualUpdatePayloadRef.current = payload;
    mutateLinkedInteractionUpdate(payload);
  };

  useEffect(() => {
    if (!openDialog) {
      setSelectedInteractionId(null);
      setShowLinkedInteraction(false);
      return;
    }
    if (isNewActionItem) return;
    setSelectedInteractionId(actionItemDetails?.interactionId ?? null);
    setShowLinkedInteraction(!!actionItemDetails?.interactionId);
  }, [actionItemDetails?.actionItemId, actionItemDetails?.interactionId, openDialog, isNewActionItem]);

  useEffect(() => {
    if (!openDialog || isNewActionItem) return;
    const nextInteractionId = selectedActionItem?.interactionId ?? null;
    if (!nextInteractionId) return;
    setSelectedInteractionId(nextInteractionId);
    setShowLinkedInteraction(true);
  }, [selectedActionItem?.id, selectedActionItem?.interactionId, openDialog, isNewActionItem]);

  useEffect(() => {
    if (isNewActionItem || isSupportTask) {
      focusTitleInput();
    }
  }, [isNewActionItem, isSupportTask, openDialog]);

  useEffect(() => {
    if (!shouldRestoreTitleFocusRef.current || showSpinner) return;
    focusTitleInput();
  }, [showSpinner, actionItem?.id, actionItemDetails?.actionItemId, openDialog]);

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ padding: "5px" }}
        className="action-item-grid-container"
      >
        {/* Left Section */}
        <Grid item xs={7}>
        <Box className="left-section-scroll" ref={leftSectionRef}>
          {bulkTasks ?
          <BulkTasks onTasksChange={setBulkTaskTitles} />
          : (<>
          <SlideOverHeaderLeft>
            {!bulkTasks && !isNewActionItem && (
              <ActionItemBreadcrumbs
                crumbs={ancestorCrumbs}
                currentTitle={
                  detailValues.actionItemTitle ||
                  actionItemDetails?.actionItemTitle
                }
                isSubtask={isCurrentlySubtask}
                onSelect={handleBreadcrumbSelect}
              />
            )}
          </SlideOverHeaderLeft>
          <Box sx={{ marginBottom: "24px", paddingTop: "8px" }}>
            <Box className="action-item-title-box">
              <TextField
                fullWidth
                variant="outlined"
                value={detailValues.actionItemTitle}
                onChange={(e) =>{
                  const value = e.target.value;
                  setDetailValues((prev) => ({
                    ...prev,
                    actionItemTitle: e.target.value,
                  }))
                  if (isNewActionItem || isSupportTask) {
                    setHasLeftSectionChanges(true);
                    onUnsavedChangesChange?.(true);
                  }
                  
                setErrors((prev) => ({
                  ...prev,
                  actionItemTitle: value.trim() === ""
                }));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSaveTitle();
                  }
                }}
                error={!!errors.actionItemTitle}
                helperText={errors.actionItemTitle && "Title is required"}
                label={
                  <>
                    {detailValues.actionItemTitle?.trim() ? "Title" : isSupportTask ? "Support Ticket" : "Add Title"}{" "}
                     <span style={{ color: "red" }}>*</span>
                  </>
                }
                InputProps={{
                  readOnly: !(canEdit || isNewActionItem), // allows new/support task title input
                  endAdornment: !isNewActionItem && isTitleEditing && (
                    <InputAdornment position="end">
                      <Button
                        variant="contained"
                        size="small"
                        className="save-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveTitle();
                        }}
                      >
                        Save
                        <img src={EnterIcon} alt="enter" className="enter-icon" />
                      </Button>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiInputBase-root": {
                    fontFamily: "var(--font-family)",
                    pointerEvents: !(canEdit || isNewActionItem) ? "none" : "auto", // disables hover + click
                  },
              
                  // keep same border color always
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#ced4da",
                    },
                    
                  },
              
                  // consistent label styling
                  "& .MuiInputLabel-outlined": {
                    fontSize: "0.9rem",
                    top: 0,
                    backgroundColor: "#fff",
                    padding: "0 4px",
                    zIndex: 1,
                    color: "#555",
                  },
                }}
                multiline
                minRows={1}
                inputRef={inputRef}
                onFocus={() => {
                  shouldRestoreTitleFocusRef.current = false;
                }}
              />
            </Box>
          </Box>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <Typography className="field-label">
                Description
              </Typography>
            </Box>

            {/* Textarea */}
            <Box sx={{ 
              border: "1px solid #e0e0e0",
              marginBottom: "24px",
              minHeight: "80px",
              maxHeight: "210px",
              overflowY: "auto",
              scrollbarWidth: "thin",
              cursor: showEditor && (canEdit || isNewActionItem) ? "text" : (canEdit || isNewActionItem) ? "pointer" : "default",
            }}
            className="common-editable-border"
             ref={containerRef}
             onClick={() => {
               if (showEditor && (canEdit || isNewActionItem)) {
                 // If editor is showing, focus it when clicking anywhere in the container
                 const editorElement = containerRef.current?.querySelector('.ProseMirror');
                 if (editorElement instanceof HTMLElement) {
                   editorElement.focus();
                 }
               }
             }}>
            {!showEditor ? (
            <Box
              sx={{
                paddingY: 3,
                paddingX: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: canEdit || isNewActionItem ? "pointer" : "default",
              }}
              onClick={() => {
                if (canEdit ||  isNewActionItem) setShowEditor(true)
              }}
            >
              {!detailValues.actionItemDescription?.trim() && (
                <EventNoteIcon sx={{ color: "#9e9e9e" }} />
              )}
              <Box
                sx={{
                  color: detailValues.actionItemDescription?.trim() ? "#33333b" : "#9e9e9e",
                  fontSize: "14px",
                  "& ol": { paddingLeft: "20px", listStyleType: "decimal", margin: 0 },
                  "& ul": { paddingLeft: "20px", listStyleType: "disc", margin: 0 },
                  "& li": { marginBottom: "4px" },
                  "& h1": { fontSize: "24px", fontWeight: 600, margin: "8px 0" },
                  "& h2": { fontSize: "20px", fontWeight: 600, margin: "8px 0" },
                  "& h3": { fontSize: "18px", fontWeight: 600, margin: "6px 0" },
                  "& p": { margin: "6px 0" },
                }}
                dangerouslySetInnerHTML={{
                  __html: detailValues.actionItemDescription?.trim()
                    ? detailValues.actionItemDescription
                    : "Add Description...",
                }}
              />
            </Box>
          ) : (
            <div ref={editorRef}>
            <RichTextEditor
              editorRef={(editor) => {
                descriptionEditorRef.current = editor;
              }}
              content={detailValues.actionItemDescription || ""}
              onContentUpdate={(documentHTMLContent) => {
                setDetailValues((prev) => ({
                  ...prev,
                  actionItemDescription: documentHTMLContent,
                }));
                if (isNewActionItem || isSupportTask) {
                  setHasLeftSectionChanges(true);
                  onUnsavedChangesChange?.(true);
                }
              }}              
              isEditable={canEdit}
              hideMenu={false}
              isActive={true}
              isDefaultHidingBorder={true}
              hideCopyButton={true}
              toolbarPosition="top"
              parentPage= "ActionItems"
              activeUsers={activeUsers}
              autoFocus={true}
            />
            </div>
          )}
          </Box>
          {canEdit && isDescriptionChanged && !isNewActionItem && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
              <button
                className={`btn btn-purple-compact ${detailValues.actionItemDescription?.trim() === actionItemDetails?.actionItemDescription?.trim() ? "disabled-look" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent editor from stealing focus
                  const editorElement = editorRef.current?.querySelector('.ProseMirror');
                  if (editorElement instanceof HTMLElement) {
                    editorElement.blur(); // force content commit
                  }
                  const latestDescriptionHtml = descriptionEditorRef.current?.getHTML?.() || detailValues.actionItemDescription || "";
                  if (!latestDescriptionHtml?.trim()) return;

                  const description = {
                    ...(latestDescriptionHtml && {
                      description: latestDescriptionHtml,
                    }),
                  };
                  if (actionItem?.id) {
                    triggerManualUpdate({
                      ...description,
                    });
                  }
                }}
                disabled={detailValues.actionItemDescription?.trim() === actionItemDetails?.actionItemDescription?.trim()}
              >
                Save
              </button>
              <button
                className="btn btn-outlined-small"
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent editor from stealing focus
                  const editorElement = editorRef.current?.querySelector('.ProseMirror');
                  if (editorElement instanceof HTMLElement) {
                    editorElement.blur();
                  }
                  setDetailValues(prev => ({
                    ...prev,
                    actionItemDescription: actionItemDetails?.actionItemDescription || "",
                  }));
                }}
              >
                Cancel
              </button>
            </Box>
          )}
          {!isCurrentlySubtask &&
            !selectedInteractionId &&
            !showLinkedInteraction &&
            (canEdit || isNewActionItem) && (
              <Box sx={{ mb: 1 }}>
            <Box className="attachment-container">
              <Typography className="field-label">
                Linked Interaction
              </Typography>
              <Tooltip title="Link an Interaction" placement="top" arrow>
                <IconButton
                  onClick={() => setShowLinkedInteraction(true)}
                  size="small"
                  disableRipple
                >
                  <img src={AddIcon} alt="Add" className="add-icon" />
                </IconButton>
              </Tooltip>
            </Box>
            </Box>
          )}
          {(isPersisted || (showLinkedInteraction && !isCurrentlySubtask)) && (
            <Box sx={{ mb: 3 }}>
              <Typography className="field-label" sx={{ mb: 1 }}>
                Linked Interaction
              </Typography>
              <LinkedInteraction
                interactionId={selectedInteractionId || actionItemDetails?.interactionId}
                interactionTitle={actionItemDetails?.interactionTitle}
                duration={actionItemDetails?.interactionDuration}
                startTime={actionItemDetails?.interactionStartTime}
                onSelectInteraction={(id, details) => {
                  handleInteractionSelection(id || null, details);
                }}
                canEdit={(canEdit || isNewActionItem) && !isCurrentlySubtask}
              />
            </Box>
          )} 
          <AttachmentList
          attachments={attachments}
          onUpdate={handleAttachmentInputChange}
          onRemove={handleRemoveField}
          onAdd={handleAttachmentAdd}
          isPreview={!canEditRightSection}
          apiAttachments={actionItemDetails?.attachments}
          meetingId={selectedActionItem?.meeting_id}
          actionItemId={actionItemDetails?.actionItemId}
          onUploadStatusChange={setShowSpinner}
          pendingAttachments={pendingAttachments}
          onAddPendingAttachment={(file) =>
            setPendingAttachments(prev => [...prev, file])
          }
          openDialog={openDialog}
          canEdit={canEdit}
          onSaveAttachments={saveUrlAttachmentsOnly}
          onCancelAttachments={() => {
            setAttachments(
              (actionItemDetails?.attachments || []).reduce((acc, a) => {
                const id = a.id ?? a.path ?? a.url ?? uuid();
                acc[id] = { ...a, id, url: a.url, description: a.description, isFromApi: true };
                return acc;
              }, {} as Record<string, Attachment>)
            );
          }}
        />
        {!isNewActionItem && !isCurrentlySubtask && (
            <ActionItemSubtasks
              subtasks={subtasks}
              canAdd={canAddSubtask}
              isTask={isTask}
              isAdding={isAddingSubtask}
              newTitle={newSubtaskTitle}
              onNewTitleChange={setNewSubtaskTitle}
              newAssignee={newSubtaskAssignee}
              onNewAssigneeChange={setNewSubtaskAssignee}
              newDueDate={newSubtaskDueDate}
              onNewDueDateChange={setNewSubtaskDueDate}
              newStatus={newSubtaskStatus}
              onNewStatusChange={setNewSubtaskStatus}
              users={activeUsers}
              onStartAdd={() => {
                setIsAddingSubtask(true);
                setNewSubtaskTitle("");
                setNewSubtaskAssignee(loggedInProfileOrgId || detailValues.assignee || null);
                const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");
                setNewSubtaskDueDate(tomorrow);
                setNewSubtaskStatus("to_do");
              }}
              onCancelAdd={() => {
                setIsAddingSubtask(false);
                setNewSubtaskTitle("");
                setNewSubtaskAssignee(null);
                setNewSubtaskDueDate(null);
                setNewSubtaskStatus("to_do");
              }}
              onSaveAdd={handleSaveSubtask}
              onOpenSubtask={handleOpenSubtask}
            />
          )}
        </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography className="field-label">
              Activity
            </Typography>
          </Box>
            <Tabs
              value={selectedTab}
              onChange={(_e, val) => setSelectedTab(val)}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#6B7280",
                  paddingLeft: 0,
                }
              }}
              TabIndicatorProps={{
                sx: {
                  backgroundColor:"#6036D6",
                },
              }}
            >
              <Tab 
                value="comments"
                label={
                  <Typography
                    className="integration__tab-text"
                      sx={{
                        color:
                          selectedTab === "comments"
                            ? "#6036D6"
                            : "#6F6F7F",
                        fontWeight: 600,
                      }}
                    >
                      Comments
                  </Typography>
                }
              />
              <Tab
                value="history"
                label={
                  <Typography
                    className="integration__tab-text"
                      sx={{
                        color:
                          selectedTab === "history"
                            ? "#6036D6"
                            : "#6F6F7F",
                        fontWeight: 600,
                      }}
                    >
                      History
                  </Typography>
                }
              />
            </Tabs>

            <Box sx={{ mt: 2 }}>
              {selectedTab === "comments" && (
                <ActionItemNotes
                  actionItemNotes={actionItemNotes}
                  setActionItemNotes={setActionItemNotes}
                  isPreview={!canEdit}
                  newActionItemNote={newActionItemNote}
                  setNewActionItemNote={setNewActionItemNote}
                  hideMenu={hideMenu}
                  setHideMenu={setHideMenu}
                  selectedActionItem={
                    viewedActionItemId
                      ? {
                          ...selectedActionItem,
                          id: viewedActionItemId,
                          description: detailValues?.actionItemTitle || actionItemDetails?.actionItemTitle || selectedActionItem?.description,
                        }
                      : selectedActionItem
                  }
                  isNewActionItem={isNewActionItem}
                  activeUsers={activeUsers}
                />
              )}

              {selectedTab === "history" && (
                <ActionItemHistory
                  actionItemId={viewedActionItemId || selectedActionItem?.id}
                />
              )}
            </Box>
          </>
          )}
        </Box>
        </Grid>

        {/* Right Section */}
        <Grid item xs={12} md={4}>
          <Box className="details-card">
            <Box sx={{display: "flex", justifyContent: "space-between"}}>
              <Typography className="details-header">
                Details
              </Typography>
              {canEdit && isTask && (
              <Box
                onClick={canEdit ? handleDelete : undefined}
                sx={{ 
                  cursor: canEdit? "pointer" : "not-allowed",
                }}>
                  <Tooltip title="Delete Task">
                    <img src={Delete} width="12" height="12"/>
                  </Tooltip>
              </Box>
              )}
              <CustomConfirmation
                isOpen={openDeletePopup}
                title="Do you want to delete this task?"
                width="350px"
                actionBtnLabel="Delete"
                cancelBtnLabel="Cancel"
                handleSuccess={handleDeleteSuccess}
                handleClose={()=> setOpenDeletePopup(false)}
                backgroundcolor = {false}
              />
            </Box>

            <Box className="details-container">
              {/* Due Date */}
              <Box className="detail-row">
                <img src={DueDateIcon} alt="Due Date" className="detail-icon" />
                <Typography  className="detail-label">
                  Due Date <span style={{ color: "red" }}>*</span>
                </Typography>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  {/* Hidden DatePicker */}
                  <CommonDatePicker
                    value={detailValues.dueDate}
                    onChange={(newDate) => {
                      const formattedDate = newDate
                        ? dayjs(newDate as string).format("YYYY-MM-DD")
                        : null;
                      setDetailValues((prev) => ({
                        ...prev,
                        dueDate: formattedDate,
                      }));

                      if (formattedDate) {
                        setErrors((prev) => ({ ...prev, dueDate: false }));
                      }
                      onAutoSave("dueDate", formattedDate);
                    }}
                    onClose={() => setOpenDatePicker(false)}
                    anchorEl={openDatePicker ? dateAnchorRef.current : null}
                  />

                  {/* Displayed date */}
                  <Box
                  ref={dateAnchorRef}
                  onClick={() => {
                    canEditRightSection && setOpenDatePicker(true)}}
                  sx={{
                    border: errors.dueDate ? "1px solid red" : "1px solid #e0e0e0",
                  }}
                  className= "background-colour-box common-editable-border"
                >
                 {detailValues.dueDate ? (
                  <Typography
                    className="detail-label due-date-selected"
                    sx={{
                      color: !canEditRightSection ? "#999999" : "#43434D",
                      fontSize: "14px",
                      fontWeight: 400,
                      minWidth: "90px",
                    }}
                  >
                    {dayjs(detailValues.dueDate, "YYYY-MM-DD").format("MMM DD YYYY")}
                  </Typography>
                ) : (
                  <Typography
                    className="detail-label due-date-placeholder priority-label"
                    sx={{
                      minWidth: "90px",
                    }}
                  >
                    Select
                  </Typography>
                )}
                  <img src={CalendarIcon} alt="" />
                </Box>
                </LocalizationProvider>
              </Box>

              {/* Priority */}
              <DetailRow icon={DefaultFlagIcon} label="Priority" required error={errors.priority}>
              <PrioritySelector
                id="priority"
                activeDropdownId={activeDropdownId}
                setActiveDropdownId={setActiveDropdownId}
                value={detailValues.priority}
                onChange={(value) => {
                  setDetailValues((prev) => ({ ...prev, priority: value }));
                  setErrors((prev) => ({ ...prev, priority: false }));
                  onAutoSave("priority", value);
                }}
                disabled={!canEditRightSection}
                error={errors.priority}
                options={PRIORITY_OPTIONS}
              />
            </DetailRow>

              {/* Status */}
            <DetailRow icon={StatusIcon} label="Status" required error={errors.status}>
              <StatusSelector
                id="status"
                activeDropdownId={activeDropdownId}
                setActiveDropdownId={setActiveDropdownId}
                value={detailValues.status}
                onChange={(value) => {
                  setDetailValues((prev) => ({ ...prev, status: value }));
                  onAutoSave("status", value);
                }}
                disabled={!canEditRightSection}
                error={errors.status}
                options={STATUS_OPTIONS}
              />
            </DetailRow>

              {/* Assignee */}
              <DetailRow icon={AssigneeIcon} label="Solo Player" required error={errors.assignee}>
              <UserSelector
                value={detailValues.assignee}
                onChange={(profileOrgRelationshipId) => {
                  setDetailValues((prev) => ({ ...prev, assignee: profileOrgRelationshipId as string | null }));
                  setErrors((prev) => ({ ...prev, assignee: false }));
                  onAutoSave("assignee", (profileOrgRelationshipId as string | null) ?? null);
                }}
                users={activeUsers}
                disabled={!canEditRightSection}
                error={errors.assignee}
              />
            </DetailRow>

            {/* Account */}
            {!isSupportMode && (
              <>
            <DetailRow icon={ClientIcon} label="Account" >
              <AccountSelector
                  id="account"
                  activeDropdownId={activeDropdownId}
                  setActiveDropdownId={setActiveDropdownId}
                  value={ detailValues.client?.name || groupByValues?.groupByClientDomain?.clientName || ""}
                  onChange={(orgId, name) => {
                    setDetailValues(prev => ({
                      ...prev,
                      client: { orgId, name }
                    }));
                    onAutoSave("clientOrgId", orgId ?? null);
                  }}
                  clientList={clientList} 
                  icon={ClientIcon}
                  useDropdownBox
                  isPreview={!canEditRightSection}
                  />  
            </DetailRow>
            </>
            )}
            {!isSupportMode && (
              <>
              {/* Coach */}
              <DetailRow icon={CoachIcon} label="Solo Coach">
              <UserSelector
                value={detailValues.coach}
                onChange={(profileOrgRelationshipId) => 
                  {
                    const updatedRemovedFields = !profileOrgRelationshipId
                      ? [...new Set([...removedFields, "coach"])]
                      : removedFields.filter((field) => field !== "coach");
                    setDetailValues((prev) => ({ ...prev, coach: profileOrgRelationshipId as string | null }));
                    setRemovedFields(updatedRemovedFields);
                    onAutoSave("coach", (profileOrgRelationshipId as string | null) ?? null, updatedRemovedFields);
                  }}
                users={activeUsers}
                disabled={!canEditRightSection}
                removeField="coach"
              />
            </DetailRow>
            </>
            )}

            {!isSupportMode && (
              <>
              {/* Sponsor */}
            <DetailRow icon={SponserIcon} label="Exec Sponsor">
              <UserSelector
                value={detailValues.sponsor}
                onChange={(profileOrgRelationshipId) => 
                  {
                    const updatedRemovedFields = !profileOrgRelationshipId
                      ? [...new Set([...removedFields, "sponsor"])]
                      : removedFields.filter((field) => field !== "sponsor");
                    setDetailValues((prev) => ({ ...prev, sponsor: profileOrgRelationshipId as string | null }));
                    setRemovedFields(updatedRemovedFields);
                    onAutoSave("sponsor", (profileOrgRelationshipId as string | null) ?? null, updatedRemovedFields);
                  }}
                users={activeUsers}
                disabled={!canEditRightSection}
                position={true}
                removeField="sponsor"
              />
            </DetailRow>
            </>
            )}

              {/* Subscribers */}
              {/* Tracking (Subscribers) */}
              <DetailRow icon={SubscribeIcon} label="Subscribers">
                <UserSelector
                  value={detailValues.subscribers} // should be string[]
                  onChange={(profileOrgRelationshipId) => {
                    const newSubscribers = Array.isArray(profileOrgRelationshipId)
                      ? profileOrgRelationshipId.filter(Boolean)
                      : [];

                    setDetailValues(prev => ({
                      ...prev,
                      subscribers: newSubscribers,
                      subscribersTouched: true,
                    }));
                    onAutoSave("subscribers", newSubscribers);
                  }}
                  spacesValue={detailValues.spaces} // spaces
                  onSpacesChange={(id) => {
                    const nextSpaces = Array.isArray(id) ? id : [];
                    setDetailValues(prev => ({ ...prev, spaces: nextSpaces }));
                    onAutoSave("spaces", nextSpaces);
                  }}
                  users={activeUsers}
                  disabled={!canEditRightSection}
                  multi={true}
                  placeholder="Select"
                  spaces={localSpacesList}
                  position={true}
                />
              </DetailRow>
              {/* Tags */}
                <DetailRow icon={TagListIcon} label="Tags" >
                  <ActionItemsTagsCell
                    value={Array.isArray(detailValues.tags)
                      ? detailValues?.tags
                      .map((id) => localTagsList.find((t) => t.tagId === id))
                      .filter(Boolean)
                      : []}
                      onChange={(updatedTags) => {
                        setDetailValues((prev) => ({ ...prev, tags: updatedTags }));
                        onAutoSave("tags", updatedTags);
                      }}
                      canEdit={canEditRightSection}
                      tagsList={localTagsList}
                      row={selectedActionItem}
                      hideDefaultIcon
                      inlineSearch
                      noBorder
                      isNewTask={isNewActionItem}
                      saveButtonLabel = "Save"
                      />
                </DetailRow>
              </Box>
            </Box>
        </Grid>
      </Grid>
      <ProgressSpinner showSpinner={showSpinner} />

    </>
  );
};

export default ActionItem;
